import { apiErrorField } from '@agos/api-client'
import {
  getGetSuppliersQueryKey,
  useDeleteSuppliersId,
  useGetStockItems,
  useGetSuppliers,
  useGetSuppliersSupplierIdItems,
  usePatchSuppliersId,
  usePostSuppliers,
  usePostSuppliersBackfillFromReceipts,
  usePostSuppliersSupplierIdItems,
  useDeleteSupplierItemsId,
} from '@agos/api-client/inventory'
import { T, formatIban, tt } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  ConfirmDialog,
  DataTable,
  FilterBar,
  IconButton,
  PageHeader,
  PhoneInput,
  Spinner,
  StatCard,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Boxes,
  Edit,
  Plus,
  Power,
  RefreshCw,
  Trash2,
  Truck,
  X,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import {
  byAttention,
  completeness,
  duplicateCodes,
  filterSuppliers,
  identityProblems,
  matchesItemSearch,
  supplierPayload,
  type IdentityProblem,
  type Supplier,
  type SupplierDraft,
  type SupplierItemRow,
} from './supplier-identity'

/**
 * `/suppliers` — who the kitchen buys from, and which of their codes means what.
 *
 * Legacy's list, its 20-field editor, the activate/deactivate toggle, the delete, the
 * *backfill from receipts* action and the per-supplier code-mapping dialog.
 *
 * **FB-080** is what none of it checks. A supplier record holds the IBAN money is paid
 * to, the IČO a tax filing quotes and the IČ DPH a VAT return reconciles against, and
 * every one is an input that accepts anything. All three carry deterministic check
 * digits — verified here against the two fully-filled suppliers in the restored data —
 * so a transposed digit is caught before the record is saved rather than when the
 * supplier rings about a payment that never arrived.
 *
 * Ported from legacy `pages/suppliers.tsx`.
 */

export function SuppliersPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [editing, setEditing] = useState<{ supplier: Supplier | null } | null>(null)
  const [mapping, setMapping] = useState<Supplier | null>(null)
  const [deleting, setDeleting] = useState<Supplier | null>(null)
  const [backfilled, setBackfilled] = useState<string | null>(null)

  const { data, isLoading } = useGetSuppliers()
  const suppliers = useMemo<Supplier[]>(() => data ?? [], [data])

  const shown = useMemo(
    () => byAttention(filterSuppliers(suppliers, search, showInactive)),
    [suppliers, search, showInactive],
  )

  const flagged = suppliers.filter((supplier) => identityProblems(supplier).length > 0).length
  const incomplete = suppliers.filter(
    (supplier) => completeness(supplier).missingForPayment.length > 0,
  ).length

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetSuppliersQueryKey() })
  }
  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Operácia sa nepodarila.'),
      variant: 'destructive',
    })

  const toggle = usePatchSuppliersId({ mutation: { onSuccess: invalidate, onError } })
  const remove = useDeleteSuppliersId({
    mutation: {
      onSuccess: () => {
        setDeleting(null)
        invalidate()
      },
      onError,
    },
  })
  const backfill = usePostSuppliersBackfillFromReceipts({
    mutation: {
      onSuccess: (result: unknown) => {
        const body = asRecord(result)
        setBackfilled(
          `${numberOf(body?.insertedSuppliers)} ${tt('nových dodávateľov')}, ${numberOf(
            body?.linkedReceipts,
          )} ${tt('napojených faktúr')}`,
        )
        invalidate()
      },
      onError,
    },
  })

  const columns: Column<Supplier>[] = useMemo(
    () => [
      {
        key: 'name',
        header: tt('Dodávateľ'),
        sortable: true,
        sortValue: (supplier) => supplier.name,
        render: (supplier) => (
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{supplier.name}</p>
            {supplier.city && (
              <p className="text-[11px] text-muted-foreground truncate">{supplier.city}</p>
            )}
          </div>
        ),
      },
      {
        key: 'ico',
        header: tt('IČO / DIČ'),
        sortable: true,
        sortValue: (supplier) => supplier.ico ?? '',
        render: (supplier) => (
          <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
            {supplier.ico || '-'}
            {supplier.dic ? ` / ${supplier.dic}` : ''}
          </span>
        ),
      },
      {
        key: 'iban',
        header: tt('IBAN'),
        render: (supplier) => (
          <span className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">
            {supplier.iban ? formatIban(supplier.iban) : '-'}
          </span>
        ),
      },
      {
        // FB-080 — the reason to look at a row at all.
        key: 'checks',
        header: tt('Kontrola'),
        sortable: true,
        sortValue: (supplier) => -identityProblems(supplier).length,
        render: (supplier) => {
          const problems = identityProblems(supplier)
          const missing = completeness(supplier).missingForPayment
          if (problems.length > 0) {
            return (
              <StatusBadge
                color="red"
                label={problems.map(problemLabel).join(', ')}
                icon={AlertCircle}
              />
            )
          }
          if (missing.length > 0) {
            return (
              <span className="text-[11px] text-muted-foreground">
                {tt('chýba')} {missing.join(', ')}
              </span>
            )
          }
          return <StatusBadge color="green" label={tt('v poriadku')} />
        },
      },
      {
        key: 'active',
        header: tt('Stav'),
        sortable: true,
        sortValue: (supplier) => (supplier.active === false ? 1 : 0),
        render: (supplier) =>
          supplier.active === false ? (
            <StatusBadge color="zinc" label={tt('Neaktívny')} />
          ) : (
            <StatusBadge color="green" label={tt('Aktívny')} />
          ),
      },
      {
        key: 'actions',
        header: '',
        render: (supplier) => (
          <div className="flex items-center gap-1 justify-end">
            <IconButton
              label={`${tt('Položky a kódy')} - ${supplier.name ?? ''}`}
              onClick={() => setMapping(supplier)}
            >
              <Boxes className="w-3.5 h-3.5" />
            </IconButton>
            <IconButton
              label={`${tt('Upraviť')} - ${supplier.name ?? ''}`}
              onClick={() => setEditing({ supplier })}
            >
              <Edit className="w-3.5 h-3.5" />
            </IconButton>
            <IconButton
              label={`${supplier.active === false ? tt('Aktivovať') : tt('Deaktivovať')} - ${supplier.name ?? ''}`}
              onClick={() =>
                supplier.id !== undefined &&
                toggle.mutate({ id: supplier.id, data: { active: supplier.active === false } })
              }
            >
              <Power className="w-3.5 h-3.5" />
            </IconButton>
            <IconButton
              label={`${tt('Zmazať')} - ${supplier.name ?? ''}`}
              tone="red"
              onClick={() => setDeleting(supplier)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </IconButton>
          </div>
        ),
      },
    ],
    [toggle],
  )

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Dodávatelia')}
        description={tt('Firmy, od ktorých kuchyňa nakupuje, a ich kódy na faktúrach')}
        icon={<Truck className="w-5 h-5" />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => backfill.mutate()}
              disabled={backfill.isPending}
              title={tt('Vytvorí dodávateľov z už nahraných faktúr a napojí ich na ne')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-muted/50 transition-colors disabled:opacity-50"
            >
              {backfill.isPending ? <Spinner className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
              <T>Doplniť z faktúr</T>
            </button>
            <button
              type="button"
              onClick={() => setEditing({ supplier: null })}
              className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              <Plus className="w-4 h-4" />
              <T>Nový dodávateľ</T>
            </button>
          </div>
        }
      />

      {backfilled && (
        <p className="text-xs text-emerald-500" data-backfill-result>
          <T>Doplnené:</T> {backfilled}
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-supplier-totals>
        <StatCard label={tt('Dodávateľov')} value={String(suppliers.length)} icon={Truck} color="blue" />
        <StatCard
          label={tt('Chybné údaje')}
          value={String(flagged)}
          icon={AlertCircle}
          color={flagged > 0 ? 'red' : 'emerald'}
        />
        <StatCard
          label={tt('Nedoplnené na platbu')}
          value={String(incomplete)}
          icon={AlertCircle}
          color={incomplete > 0 ? 'amber' : 'emerald'}
        />
      </div>

      {flagged > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-red-500" data-identity-warning>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          {/* FB-080 - an IBAN with a transposed digit is a payment to nowhere. */}
          <T>
            V týchto záznamoch nesedí kontrolná číslica IBAN, IČO alebo IČ DPH. Peniaze poslané na
            chybný IBAN sa nevrátia samé.
          </T>
        </p>
      )}

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: tt('Hľadať podľa názvu, IČO, mesta...'),
        }}
        filters={
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span>
              <T>Aj neaktívnych</T>
            </span>
          </label>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <div data-suppliers>
          <DataTable<Supplier>
            columns={columns}
            data={shown}
            rowKey={(supplier) => supplier.id ?? 0}
            pageSize={25}
            compact
            emptyMessage={tt('Žiadni dodávatelia')}
            emptyIcon={<Truck className="w-10 h-10 mb-3 opacity-30" />}
          />
        </div>
      )}

      {editing && (
        <SupplierEditor supplier={editing.supplier} onClose={() => setEditing(null)} />
      )}
      {mapping && <CodeMappingDialog supplier={mapping} onClose={() => setMapping(null)} />}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting?.id !== undefined && remove.mutate({ id: deleting.id })}
        title={tt('Zmazať dodávateľa?')}
        description={`${deleting?.name ?? ''} - ${tt('faktúry a mapovania kódov, ktoré na neho odkazujú, ostanú bez dodávateľa.')}`}
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={remove.isPending}
      />
    </div>
  )
}

// ─── the editor ──────────────────────────────────────────────────────────────

const FIELDS: readonly { key: keyof Supplier; label: string; placeholder?: string }[] = [
  { key: 'name', label: tt('Názov') },
  { key: 'ico', label: tt('IČO'), placeholder: '12345678' },
  { key: 'dic', label: tt('DIČ'), placeholder: '2012345678' },
  { key: 'icDph', label: tt('IČ DPH'), placeholder: 'SK2012345678' },
  { key: 'street', label: tt('Ulica') },
  { key: 'city', label: tt('Mesto') },
  { key: 'zip', label: tt('PSČ') },
  { key: 'country', label: tt('Krajina'), placeholder: 'SK' },
  { key: 'phone', label: tt('Telefón') },
  { key: 'email', label: tt('E-mail') },
  { key: 'website', label: tt('Web') },
  { key: 'bankName', label: tt('Banka') },
  { key: 'bankSwift', label: 'SWIFT' },
  { key: 'iban', label: 'IBAN', placeholder: 'SK00 0000 0000 0000 0000 0000' },
  { key: 'licenseAlcohol', label: tt('Licencia na alkohol') },
  { key: 'defaultPaymentForm', label: tt('Forma platby') },
  { key: 'defaultConstantSymbol', label: tt('Konštantný symbol') },
] as const

function SupplierEditor({
  supplier,
  onClose,
}: {
  supplier: Supplier | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  // A draft, not a response: a supplier being typed in has no id or timestamps yet, and `Supplier`
  // now says a row that came back from the API has all six. See SupplierDraft.
  const [form, setForm] = useState<SupplierDraft>(() => supplier ?? { country: 'SK', active: true })

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: getGetSuppliersQueryKey() })
    onClose()
  }
  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Dodávateľa sa nepodarilo uložiť.'),
      variant: 'destructive',
    })

  const create = usePostSuppliers({ mutation: { onSuccess: done, onError } })
  const update = usePatchSuppliersId({ mutation: { onSuccess: done, onError } })

  const problems = identityProblems(form)
  const missing = completeness(form).missingForPayment
  const pending = create.isPending || update.isPending
  const nameless = String(form.name ?? '').trim() === ''

  const save = () => {
    const payload = supplierPayload(form)
    if (supplier?.id !== undefined) {
      update.mutate({ id: supplier.id, data: payload })
      return
    }
    create.mutate({ data: payload })
  }

  return (
    <Modal
      title={supplier ? tt('Upraviť dodávateľa') : tt('Nový dodávateľ')}
      onClose={onClose}
      wide
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={save}
            disabled={pending || nameless}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {pending ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť</T>}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map((field) => (
          <label key={String(field.key)} className="space-y-1.5 block">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
              {field.label}
            </span>
            {/* One field in this generic list is a telephone number, and a generic text input hands a
              * phone user the alphabetic keyboard for it. */}
            {field.key === 'phone' ? (
              <PhoneInput
                value={String(form[field.key] ?? '')}
                onChange={(next) => setForm((current) => ({ ...current, [field.key]: next }))}
                label={field.label}
                placeholder={field.placeholder}
                testId="supplier"
              />
            ) : (
              <input
                value={String(form[field.key] ?? '')}
                onChange={(event) =>
                  setForm((current) => ({ ...current, [field.key]: event.target.value }))
                }
                placeholder={field.placeholder}
                aria-label={field.label}
                className={INPUT}
              />
            )}
          </label>
        ))}
      </div>

      <label className="space-y-1.5 block">
        <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
          {tt('Poznámka')}
        </span>
        <textarea
          value={String(form.notes ?? '')}
          onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
          aria-label={tt('Poznámka')}
          rows={2}
          className={INPUT}
        />
      </label>

      {/* FB-080 - said while it is being typed, not after the money has gone. */}
      {problems.length > 0 && (
        <div className="space-y-1" data-identity-problems>
          {problems.map((problem) => (
            <p
              key={problem.kind}
              className="flex items-start gap-1.5 text-xs text-red-500 leading-relaxed"
            >
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <ProblemText problem={problem} />
            </p>
          ))}
        </div>
      )}

      {problems.length === 0 && missing.length > 0 && (
        <p className="text-xs text-muted-foreground">
          <T>Na prevodný príkaz ešte chýba:</T> {missing.join(', ')}
        </p>
      )}

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={form.active !== false}
          onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))}
          className="w-4 h-4 rounded"
        />
        <span className="text-sm">
          <T>Aktívny dodávateľ</T>
        </span>
      </label>
    </Modal>
  )
}

function ProblemText({ problem }: { problem: IdentityProblem }) {
  if (problem.kind === 'ico-checksum') {
    return (
      <span>
        <T>IČO</T> <strong>{problem.value}</strong>{' '}
        <T>nemá platnú kontrolnú číslicu - skontrolujte, či nie sú dve číslice prehodené.</T>
      </span>
    )
  }
  if (problem.kind === 'dic-length') {
    return (
      <span>
        <T>DIČ má mať desať číslic, toto má</T> {problem.value.replace(/\D/g, '').length}.
      </span>
    )
  }
  if (problem.kind === 'ic-dph-shape') {
    return (
      <span>
        <T>IČ DPH má mať tvar SK a desať číslic.</T>
      </span>
    )
  }
  if (problem.kind === 'ic-dph-mismatch') {
    return (
      <span>
        <T>IČ DPH sa nezhoduje s DIČ - podľa DIČ by malo byť</T>{' '}
        <strong>{problem.expected}</strong>.
      </span>
    )
  }
  if (problem.kind === 'iban-length') {
    return (
      <span>
        <T>Slovenský IBAN má 24 znakov, tento má</T>{' '}
        {problem.value.replace(/\s/g, '').length}.
      </span>
    )
  }
  return (
    <span>
      <T>
        IBAN nemá platnú kontrolnú číslicu. Platba na takýto účet neprejde alebo pôjde niekam inam.
      </T>
    </span>
  )
}

// ─── the code mapping ────────────────────────────────────────────────────────

function CodeMappingDialog({
  supplier,
  onClose,
}: {
  supplier: Supplier
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const supplierId = supplier.id ?? 0
  const [search, setSearch] = useState('')
  const [code, setCode] = useState('')
  const [stockItemId, setStockItemId] = useState('')
  const [supplierName, setSupplierName] = useState('')
  const [unit, setUnit] = useState('')

  const { data: itemData, isLoading } = useGetSuppliersSupplierIdItems(supplierId)
  const { data: stockData } = useGetStockItems({ active: 'true' })

  const rows = useMemo<SupplierItemRow[]>(() => itemData ?? [], [itemData])
  const stockItems = useMemo<{ id?: number; name?: string; unit?: string }[]>(
    () => stockData ?? [],
    [stockData],
  )
  const shown = useMemo(
    () => rows.filter((row) => matchesItemSearch(row, search)),
    [rows, search],
  )
  const duplicates = duplicateCodes(rows)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['/api/v1/suppliers'] })
  }
  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Operácia sa nepodarila.'),
      variant: 'destructive',
    })

  const add = usePostSuppliersSupplierIdItems({
    mutation: {
      onSuccess: () => {
        setCode('')
        setStockItemId('')
        setSupplierName('')
        setUnit('')
        reload()
      },
      onError,
    },
  })
  const remove = useDeleteSupplierItemsId({ mutation: { onSuccess: reload, onError } })

  const alreadyMapped = duplicates.includes(code.trim().toLowerCase()) ||
    rows.some((row) => (row.supplierCode ?? '').trim().toLowerCase() === code.trim().toLowerCase())
  const ready = code.trim() !== '' && stockItemId !== ''

  return (
    <Modal title={`${tt('Položky a kódy')} - ${supplier.name ?? ''}`} onClose={onClose} wide footer={null}>
      <div className="bg-muted/40 rounded-xl px-4 py-3 text-xs text-muted-foreground space-y-1">
        <p>
          <T>
            Tu si nastavíte, ktorý kód na faktúre od tohto dodávateľa znamená ktorú skladovú
            položku. Pri ďalšom nahraní faktúry sa riadky podľa toho samé priradia.
          </T>
        </p>
      </div>

      {duplicates.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-duplicate-codes>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            {/* One code must mean one item, or the next import is a coin toss. */}
            <T>Tieto kódy sú namapované viackrát, takže pri importe nie je jasné, čo znamenajú:</T>{' '}
            <strong>{duplicates.join(', ')}</strong>
          </span>
        </p>
      )}

      <div className="bg-card border border-border rounded-xl p-4 space-y-3">
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          <T>Pridať mapovanie</T>
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="space-y-1.5 block">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
              {tt('Kód na faktúre')}
            </span>
            <input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              aria-label={tt('Kód na faktúre')}
              placeholder="055049831"
              className={INPUT}
            />
          </label>
          <label className="space-y-1.5 block">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
              {tt('Skladová položka')}
            </span>
            <select
              value={stockItemId}
              onChange={(event) => setStockItemId(event.target.value)}
              aria-label={tt('Skladová položka')}
              className={INPUT}
            >
              <option value="">{tt('- Vyberte položku -')}</option>
              {stockItems.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5 block">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
              {tt('Názov u dodávateľa')}
            </span>
            <input
              value={supplierName}
              onChange={(event) => setSupplierName(event.target.value)}
              aria-label={tt('Názov u dodávateľa')}
              className={INPUT}
            />
          </label>
          <label className="space-y-1.5 block">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
              {tt('Jednotka na faktúre')}
            </span>
            <input
              value={unit}
              onChange={(event) => setUnit(event.target.value)}
              aria-label={tt('Jednotka na faktúre')}
              className={INPUT}
            />
          </label>
        </div>

        {alreadyMapped && code.trim() !== '' && (
          <p className="text-xs text-amber-500" data-code-already-mapped>
            <T>Tento kód už namapovaný je - pridaním vznikne druhý význam pre ten istý kód.</T>
          </p>
        )}

        <button
          type="button"
          onClick={() =>
            add.mutate({
              supplierId,
              data: {
                stockItemId: Number(stockItemId),
                supplierCode: code.trim(),
                supplierName: supplierName.trim() || null,
                defaultUnit: unit.trim() || null,
              },
            })
          }
          disabled={!ready || add.isPending}
          className={cn('px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {add.isPending ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Pridať</T>}
        </button>
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: tt('Hľadať kód alebo položku...') }}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div data-code-mappings>
          <DataTable<SupplierItemRow>
            columns={[
              {
                key: 'supplierCode',
                header: tt('Kód'),
                sortable: true,
                sortValue: (row) => row.supplierCode ?? '',
                render: (row) => (
                  <span
                    className={cn(
                      'font-mono text-xs font-bold',
                      duplicates.includes((row.supplierCode ?? '').trim().toLowerCase())
                        ? 'text-amber-500'
                        : 'text-primary',
                    )}
                  >
                    {row.supplierCode}
                  </span>
                ),
              },
              {
                key: 'stockItemName',
                header: tt('Skladová položka'),
                sortable: true,
                sortValue: (row) => row.stockItemName ?? '',
                render: (row) => (
                  <span className="text-sm truncate">{row.stockItemName ?? '-'}</span>
                ),
              },
              {
                key: 'supplierName',
                header: tt('Názov u dodávateľa'),
                render: (row) => (
                  <span className="text-xs text-muted-foreground truncate">
                    {row.supplierName ?? '-'}
                  </span>
                ),
              },
              {
                key: 'defaultUnit',
                header: tt('Jednotka'),
                render: (row) => (
                  <span className="text-xs text-muted-foreground">{row.defaultUnit ?? '-'}</span>
                ),
              },
              {
                key: 'actions',
                header: '',
                render: (row) => (
                  <div className="flex justify-end">
                    <IconButton
                      label={`${tt('Odstrániť mapovanie')} ${row.supplierCode ?? ''}`}
                      tone="red"
                      onClick={() => row.id !== undefined && remove.mutate({ id: row.id })}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </IconButton>
                  </div>
                ),
              },
            ]}
            data={shown}
            rowKey={(row) => row.id ?? 0}
            pageSize={10}
            compact
            emptyMessage={tt('Žiadne mapovania')}
          />
        </div>
      )}
    </Modal>
  )
}

// ─── small pieces ────────────────────────────────────────────────────────────

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Modal({
  title,
  onClose,
  footer,
  wide,
  children,
}: {
  title: string
  onClose: () => void
  footer: ReactNode
  wide?: boolean
  children: ReactNode
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={cn(
          'bg-card border border-border rounded-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl',
          wide ? 'max-w-4xl' : 'max-w-lg',
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 className="font-bold text-sm truncate">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">{children}</div>
        {footer && <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">{footer}</div>}
      </div>
    </div>
  )
}


function problemLabel(problem: IdentityProblem): string {
  if (problem.kind === 'ico-checksum') return tt('IČO')
  if (problem.kind === 'dic-length') return tt('DIČ')
  if (problem.kind === 'ic-dph-shape' || problem.kind === 'ic-dph-mismatch') return tt('IČ DPH')
  return 'IBAN'
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
}

function numberOf(value: unknown): number {
  return typeof value === 'number' ? value : 0
}

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record?.error ?? record?.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string, so every
 * refusal reached the operator as *"HTTP 400 Bad Request: <the real sentence>"*, which is exactly
 * what `apiErrorMessage` exists to avoid.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
